import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Linkedin, Search, Users, Loader2, AlertTriangle, ExternalLink, UserPlus, Sparkles, Trophy, MessageSquareQuote, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import rewardfulLogo from "@/assets/rewardful-logo.png";
import stripePremierPartner from "@/assets/stripe-premier-partner.svg";
import { track } from "@/lib/analytics";
import { ToolTabs } from "@/components/ToolTabs";

const SEARCH_STAGES = [
  { label: "Searching LinkedIn…", ms: 4000 },
  { label: "Filtering by followers…", ms: 4000 },
  { label: "Ranking by topical fit…", ms: 6000 },
  { label: "Almost done…", ms: 6000 },
] as const;

type Creator = {
  name: string;
  profileUrl: string;
  avatar: string | null;
  followers: number | null;
  snippet: string;
  fitScore?: number;
  reason?: string;
};

export const RECIPIENT_STORAGE_KEY = "outreach:recipient";

const FindCreators = () => {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [minFollowers, setMinFollowers] = useState("");
  const [maxFollowers, setMaxFollowers] = useState("");
  const [findingCreators, setFindingCreators] = useState(false);
  const [searchStageIdx, setSearchStageIdx] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [creators, setCreators] = useState<Creator[] | null>(null);
  const [creatorError, setCreatorError] = useState<string | null>(null);

  useEffect(() => {
    const PAGE_URL = "https://outreach.rewardful.com/find-creators";
    const HOME_URL = "https://outreach.rewardful.com/";
    const PAGE_TITLE = "Find LinkedIn Creators - Rewardful";
    const desc =
      "Free AI tool: search LinkedIn creators by niche, filter by follower count, and get the top 15 ranked by topical fit - then hand off to the outreach generator.";

    document.title = PAGE_TITLE;

    const setMeta = (selector: string, attr: string, value: string, create: () => HTMLElement) => {
      let el = document.head.querySelector(selector) as HTMLElement | null;
      if (!el) {
        el = create();
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };
    const metaByName = (name: string) => () => {
      const m = document.createElement("meta");
      m.setAttribute("name", name);
      return m;
    };
    const metaByProp = (prop: string) => () => {
      const m = document.createElement("meta");
      m.setAttribute("property", prop);
      return m;
    };

    setMeta('meta[name="description"]', "content", desc, metaByName("description"));
    setMeta('link[rel="canonical"]', "href", PAGE_URL, () => {
      const l = document.createElement("link");
      l.setAttribute("rel", "canonical");
      return l;
    });
    setMeta('meta[property="og:title"]', "content", PAGE_TITLE, metaByProp("og:title"));
    setMeta('meta[property="og:description"]', "content", desc, metaByProp("og:description"));
    setMeta('meta[property="og:url"]', "content", PAGE_URL, metaByProp("og:url"));
    setMeta('meta[name="twitter:title"]', "content", PAGE_TITLE, metaByName("twitter:title"));
    setMeta('meta[name="twitter:description"]', "content", desc, metaByName("twitter:description"));

    // JSON-LD: second SoftwareApplication + BreadcrumbList for /find-creators
    const ldId = "ld-find-creators";
    document.getElementById(ldId)?.remove();
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = ldId;
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "SoftwareApplication",
          name: "LinkedIn Creator Finder",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          url: PAGE_URL,
          description:
            "Free AI tool that searches LinkedIn creators by niche, filters by follower count, and ranks the top 15 by topical fit for affiliate recruitment.",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          isPartOf: {
            "@type": "WebSite",
            name: "Affiliate Outreach Generator",
            url: HOME_URL,
          },
          publisher: { "@type": "Organization", name: "Rewardful", url: "https://www.rewardful.com" },
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Affiliate Outreach Generator",
              item: HOME_URL,
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Find LinkedIn Creators",
              item: PAGE_URL,
            },
          ],
        },
      ],
    });
    document.head.appendChild(script);

    return () => {
      document.getElementById(ldId)?.remove();
    };
  }, []);



  // Progressive loading stages + elapsed counter
  useEffect(() => {
    if (!findingCreators) {
      setSearchStageIdx(0);
      setElapsed(0);
      return;
    }
    let i = 0;
    let stageTimeout: number;
    const advance = () => {
      i = Math.min(i + 1, SEARCH_STAGES.length - 1);
      setSearchStageIdx(i);
      if (i < SEARCH_STAGES.length - 1) {
        stageTimeout = window.setTimeout(advance, SEARCH_STAGES[i].ms);
      }
    };
    stageTimeout = window.setTimeout(advance, SEARCH_STAGES[0].ms);
    const start = Date.now();
    const tick = window.setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 500);
    return () => {
      window.clearTimeout(stageTimeout);
      window.clearInterval(tick);
    };
  }, [findingCreators]);

  const handleFindCreators = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreatorError(null);
    const k = keyword.trim();
    if (!k) {
      setCreatorError("Enter a keyword or niche to search.");
      return;
    }
    setFindingCreators(true);
    setCreators(null);
    track("outreach_tool_creator_search", { keyword: k });
    try {
      const min = minFollowers.trim() ? parseInt(minFollowers, 10) : undefined;
      const max = maxFollowers.trim() ? parseInt(maxFollowers, 10) : undefined;
      const { data, error: fnErr } = await supabase.functions.invoke("find-creators", {
        body: {
          keyword: k,
          ...(Number.isFinite(min) ? { minFollowers: min } : {}),
          ...(Number.isFinite(max) ? { maxFollowers: max } : {}),
        },
      });
      if (fnErr) {
        let message = fnErr.message || "Search failed";
        const ctx = (fnErr as { context?: unknown }).context;
        if (ctx instanceof Response) {
          try {
            const body = await ctx.clone().json();
            if (body?.error) message = body.error;
          } catch { /* ignore */ }
        }
        setCreatorError(message);
        return;
      }
      if ((data as any)?.error) {
        setCreatorError((data as any).error);
        return;
      }
      const list: Creator[] = (data as any)?.creators ?? [];
      setCreators(list);
      track("outreach_tool_creator_search_results", { keyword: k, count: list.length });
      if (list.length === 0) setCreatorError("No creators found for that keyword. Try something broader.");
    } catch (err) {
      setCreatorError(err instanceof Error ? err.message : "Search failed");
    } finally {
      setFindingCreators(false);
    }
  };

  const handleGenerateForCreator = (c: Creator) => {
    const recipient = { name: c.name, profileUrl: c.profileUrl, snippet: c.snippet, reason: c.reason };
    try {
      sessionStorage.setItem(RECIPIENT_STORAGE_KEY, JSON.stringify(recipient));
    } catch { /* ignore */ }
    track("outreach_tool_creator_selected", { name: c.name, fit_score: c.fitScore });
    toast({ title: `Recipient set: ${c.name}`, description: "Fill in your product URL & commission, then Generate." });
    navigate("/");
  };

  return (
    <div className="min-h-screen flex flex-col bg-brand-bg">
      <header className="sticky top-0 z-50 bg-white/85 backdrop-blur border-b border-brand-border">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-14 flex items-center justify-between">
          <a href="/" className="flex items-center" aria-label="Rewardful">
            <img src={rewardfulLogo} alt="Rewardful Logo" width={127} height={28} className="h-7 w-auto" decoding="async" />
          </a>
          <a
            href="https://www.rewardful.com/?utm_source=outreach_generator&utm_medium=tool&utm_campaign=trial_conversion&utm_content=header_cta"
            className="inline-flex items-center justify-center h-9 px-4 rounded-[5px] bg-brand-blue hover:bg-brand-blue/90 text-white text-sm font-semibold transition-colors"
          >
            Try Rewardful
          </a>
        </div>
      </header>

      <main id="main" role="main" className="flex-1">
        <section className="max-w-3xl mx-auto px-5 sm:px-8 pt-14 sm:pt-20 pb-6 text-center">
          <span className="inline-flex items-center gap-1.5 bg-brand-yellow text-brand-navy text-xs font-semibold px-3 py-1 rounded-full tracking-wide uppercase mb-5">
            <Sparkles className="h-3.5 w-3.5" />
            Free AI Tool
          </span>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-brand-navy leading-[1.05]">
            Find LinkedIn Creators
          </h1>
          <p className="mt-5 text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Enter a niche or keyword. AI ranks the top 15 LinkedIn creators by topical fit, then hands them off to the outreach generator.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1.5"><Trophy className="h-4 w-4 text-brand-blue" /> Top 15 ranked by fit</span>
            <span className="hidden sm:inline text-brand-border">•</span>
            <span className="inline-flex items-center gap-1.5"><MessageSquareQuote className="h-4 w-4 text-brand-blue" /> Explains the "why"</span>
            <span className="hidden sm:inline text-brand-border">•</span>
            <span className="inline-flex items-center gap-1.5"><Zap className="h-4 w-4 text-brand-blue" /> Hands off to generator</span>
          </div>
        </section>

        <div className="pb-8">
          <ToolTabs />
        </div>


        <section className="max-w-2xl mx-auto px-5 sm:px-8 pb-20">
          <div className="bg-card border border-brand-border rounded-2xl shadow-sm p-6 sm:p-8">
            <div className="flex items-center gap-2 mb-1">
              <Linkedin className="h-5 w-5 text-brand-blue" />
              <h2 className="text-lg font-semibold text-brand-navy">Search creators</h2>
              <span className="ml-auto text-[11px] font-medium uppercase tracking-wide text-brand-blue bg-brand-blue/10 px-2 py-0.5 rounded-full">Beta</span>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              We return up to 15 creators, scored 1-10 for fit with a short reason.
            </p>
            <form className="space-y-2" onSubmit={handleFindCreators} noValidate>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  type="text"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="e.g. affiliate marketing, indie SaaS, AI agents"
                  className="h-12 flex-1"
                  disabled={findingCreators}
                  maxLength={120}
                />
                <Button
                  type="submit"
                  disabled={findingCreators}
                  className="h-12 px-5 bg-brand-blue hover:bg-brand-blue/90 text-white"
                >
                  {findingCreators ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Searching…</>
                  ) : (
                    <><Search className="h-4 w-4" /> Find creators</>
                  )}
                </Button>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground pt-1">
                <span className="inline-flex items-center gap-1 shrink-0"><Users className="h-3 w-3" /> Followers</span>
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Input
                    type="text"
                    inputMode="numeric"
                    value={minFollowers ? Number(minFollowers).toLocaleString("en-US") : ""}
                    onChange={(e) => setMinFollowers(e.target.value.replace(/[^\d]/g, ""))}
                    placeholder="Min"
                    aria-label="Minimum followers"
                    className="h-9 flex-1 min-w-0"
                    disabled={findingCreators}
                  />
                  <span className="shrink-0">to</span>
                  <Input
                    type="text"
                    inputMode="numeric"
                    value={maxFollowers ? Number(maxFollowers).toLocaleString("en-US") : ""}
                    onChange={(e) => setMaxFollowers(e.target.value.replace(/[^\d]/g, ""))}
                    placeholder="Max"
                    aria-label="Maximum followers"
                    className="h-9 flex-1 min-w-0"
                    disabled={findingCreators}
                  />
                </div>
                <span className="basis-full text-muted-foreground">Optional. Leave blank to include any follower count.</span>
              </div>
            </form>
            {creatorError && (
              <div className="mt-4 flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{creatorError}</span>
              </div>
            )}
            {findingCreators && (
              <div className="mt-6 space-y-4" aria-live="polite">
                <div className="flex items-center justify-between gap-3 rounded-xl bg-brand-blue/5 border border-brand-blue/20 px-4 py-3">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <Loader2 className="h-4 w-4 text-brand-blue animate-spin shrink-0" />
                    <span className="text-sm font-medium text-brand-navy truncate">
                      {SEARCH_STAGES[searchStageIdx].label}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="flex items-center gap-1 translate-y-[1px]" aria-hidden="true">
                      <span className="h-1.5 w-1.5 rounded-full bg-brand-blue animate-bounce [animation-delay:-0.3s]" />
                      <span className="h-1.5 w-1.5 rounded-full bg-brand-blue animate-bounce [animation-delay:-0.15s]" />
                      <span className="h-1.5 w-1.5 rounded-full bg-brand-blue animate-bounce" />
                    </span>
                    <span className="text-xs font-mono text-muted-foreground tabular-nums">
                      {elapsed}s · usually ~20s
                    </span>
                  </div>
                </div>
                <ul className="space-y-3" aria-label="Searching creators">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <li key={i} className="flex items-start gap-3 p-3 border border-brand-border rounded-xl animate-pulse">
                      <div className="h-12 w-12 rounded-full bg-muted shrink-0" />
                      <div className="flex-1 min-w-0 space-y-2 py-1">
                        <div className="h-3.5 w-40 bg-muted rounded" />
                        <div className="h-3 w-56 bg-muted rounded" />
                        <div className="h-3 w-4/5 bg-muted rounded" />
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {creators && creators.length > 0 && (
              <ul className="mt-6 space-y-3">
                {creators.map((c) => (
                  <li key={c.profileUrl} className="flex items-start gap-3 p-3 border border-brand-border rounded-xl hover:border-brand-blue/40 transition-colors">
                    {c.avatar ? (
                      <img src={c.avatar} alt="" className="h-12 w-12 rounded-full object-cover shrink-0 bg-muted" loading="lazy" referrerPolicy="no-referrer" />
                    ) : (
                      <div className="h-12 w-12 rounded-full bg-muted shrink-0 flex items-center justify-center text-brand-navy font-semibold">
                        {c.name.slice(0, 1)}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-baseline gap-x-2">
                        <span className="font-semibold text-brand-navy truncate">{c.name}</span>
                        {typeof c.followers === "number" && (
                          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                            <Users className="h-3 w-3" />
                            {c.followers.toLocaleString("en-US")} followers
                          </span>
                        )}
                        {typeof c.fitScore === "number" && (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand-blue bg-brand-blue/10 px-2 py-0.5 rounded-full">
                            Fit {c.fitScore}/10
                          </span>
                        )}
                      </div>
                      {c.reason && (
                        <p className="mt-1 text-xs text-brand-navy/80 italic">{c.reason}</p>
                      )}
                      {c.snippet && (
                        <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{c.snippet}</p>
                      )}
                      <div className="mt-2 flex flex-wrap gap-2">
                        <a
                          href={c.profileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs font-medium text-brand-navy border border-brand-border rounded-md px-2.5 py-1 hover:border-brand-blue/40"
                        >
                          <ExternalLink className="h-3 w-3" /> View profile
                        </a>
                        <button
                          type="button"
                          onClick={() => handleGenerateForCreator(c)}
                          className="inline-flex items-center gap-1 text-xs font-medium text-white bg-brand-blue hover:bg-brand-blue/90 rounded-md px-2.5 py-1"
                        >
                          <UserPlus className="h-3 w-3" /> Generate outreach
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {!findingCreators && !creators && !creatorError && (
              <div className="mt-6 rounded-xl border border-dashed border-brand-border bg-brand-bg/50 p-5">
                <p className="text-xs font-semibold uppercase tracking-wide text-brand-navy/70 mb-3">How it works</p>
                <ol className="grid gap-3 sm:grid-cols-3 text-sm text-brand-navy/90">
                  <li className="flex gap-2">
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-brand-blue/10 text-brand-blue text-[10px] font-bold leading-none">1</span>
                    <span>Search a niche or keyword you're recruiting for.</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-brand-blue/10 text-brand-blue text-[10px] font-bold leading-none">2</span>
                    <span>AI ranks the top 15 creators 1-10 and explains the fit.</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-brand-blue/10 text-brand-blue text-[10px] font-bold leading-none">3</span>
                    <span>Hand any of them off to the outreach generator.</span>
                  </li>
                </ol>
              </div>
            )}
          </div>
        </section>

        {/* Final CTA - parity with Outreach Generator page */}
        <section className="bg-brand-navy text-white">
          <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16 sm:py-20 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold leading-tight">
              Now Track Every Affiliate You Recruit
            </h2>
            <p className="mt-4 text-white/75 text-lg max-w-2xl mx-auto">
              Finding creators is step one. Rewardful handles the rest - tracking, commissions, and payouts through Stripe or Paddle. Trusted by 3,000+ SaaS and AI companies.
            </p>
            <a
              href="https://app.getrewardful.com/signup?utm_source=outreach_generator&utm_medium=tool&utm_campaign=trial_conversion&utm_content=cta_find_creators"
              className="inline-flex items-center justify-center mt-8 h-12 px-7 rounded-[5px] bg-brand-blue hover:bg-brand-blue/90 text-white font-semibold transition-colors"
            >
              Start Your 14-Day Free Trial
            </a>
            <div className="mt-6 flex flex-col items-center gap-3">
              <img
                src={stripePremierPartner}
                alt="Stripe Premier Partner"
                width={138}
                height={24}
                loading="lazy"
                decoding="async"
                className="h-6 w-auto invert"
              />
              <p className="text-sm text-white/60">
                0% transaction fee · No credit card required
              </p>
            </div>
          </div>
        </section>
      </main>


      <footer className="border-t border-brand-border bg-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-2 text-sm text-muted-foreground">
          <p>Rewardful - Affiliate Software for SaaS</p>
          <a
            href="https://www.rewardful.com/?utm_source=outreach_generator&utm_medium=tool&utm_campaign=trial_conversion&utm_content=footer_logo"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center hover:opacity-80 transition-opacity"
            aria-label="Rewardful"
          >
            <img src={rewardfulLogo} alt="Rewardful Logo" width={109} height={24} loading="lazy" decoding="async" className="h-6 w-auto" />
          </a>
        </div>
      </footer>
    </div>
  );
};

export default FindCreators;
